import { SeriesGroupCategory } from '../../pages/home'
import { GetCandlesRequestV2 } from '../../proto/generated/candle-data-depot'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { Security } from '../../proto/generated/security'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { getNewPanesConfig } from '../../util/chart'
import { toDayStart } from '../../util/dateTime'
import { convertExpressionToDisplayExpression } from '../../util/expression'
import { getSecurityKey } from '../../util/security'
import { ChartCandle, ChartConfig, ChartMode } from './chart.typings'

const ONE_DAY_IN_SECONDS = 24 * 60 * 60

export type DayHighLowValues = { high: number; low: number }

export const supportsDayHighLowLines = (timeBucket: SuperChartConfiguration_TimeBucket): boolean =>
    getTimeWidthInSeconds(timeBucket) <= ONE_DAY_IN_SECONDS

const computeHighLowFromCandles = (candles: ChartCandle[]): DayHighLowValues | null => {
    if (!candles.length) return null
    let high = -Infinity
    let low = Infinity
    for (const c of candles) {
        if (c.high !== undefined && c.high > high) high = c.high
        if (c.low !== undefined && c.low < low) low = c.low
    }
    if (!isFinite(high) || !isFinite(low)) return null
    return { high, low }
}

export const getCurrentAndPrevDayHighLows = (
    data: ChartCandle[],
    timeBucket: SuperChartConfiguration_TimeBucket
): { currentDay: DayHighLowValues | null; prevDay: DayHighLowValues | null } => {
    if (!data.length || !supportsDayHighLowLines(timeBucket)) {
        return { currentDay: null, prevDay: null }
    }

    if (timeBucket.timeSeries === TimeBucket_TimeSeries.DAY) {
        const last = data[data.length - 1]
        const prev = data.length >= 2 ? data[data.length - 2] : null
        return {
            currentDay: last ? computeHighLowFromCandles([last]) : null,
            prevDay: prev ? computeHighLowFromCandles([prev]) : null,
        }
    }

    const dayGroups = new Map<number, ChartCandle[]>()
    data.forEach((candle) => {
        const dayStart = Math.floor(toDayStart((candle.time as number) * 1000).getTime() / 1000)
        const group = dayGroups.get(dayStart)
        if (group) group.push(candle)
        else dayGroups.set(dayStart, [candle])
    })

    const sortedDays = [...dayGroups.keys()].sort((a, b) => a - b)
    if (!sortedDays.length) return { currentDay: null, prevDay: null }

    const currentDayCandles = dayGroups.get(sortedDays[sortedDays.length - 1])!
    const prevDayCandles = sortedDays.length >= 2 ? dayGroups.get(sortedDays[sortedDays.length - 2])! : null

    return {
        currentDay: computeHighLowFromCandles(currentDayCandles),
        prevDay: prevDayCandles ? computeHighLowFromCandles(prevDayCandles) : null,
    }
}

export const updatePrimarySecurities = (config: ChartConfig, securities: Security[], mode: ChartMode, expression: string, expressionName: string) => {
    const newConfig = getNewPanesConfig(config.panes, config.panesConfig, config.seriesGroups, config.series)
    if(securities.length) {
        Object.values(newConfig.seriesGroups).forEach((sg) => {
            const securityList = sg.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? securities.map(v => getSecurityKey(v.exchange, v.code)) : sg.securityList
            const name = sg.primary ? (mode === 'EXPRESSION' ? expressionName ?? convertExpressionToDisplayExpression(expression!, securities) : securities[0].symbol || '') : sg.name
            newConfig.seriesGroups[sg.id] = {
                ...sg,
                securityList,
                name
            }
            const sid = sg.series[0]
            const series = newConfig.series[sid]
            if (sg.primary && series) {
                newConfig.series[sid] = {
                    ...series,
                    name
                }
            }
        })
    }
    return newConfig
}

export const getCandleRequestV2 = (
    backwardCandleCount: number,
    forwardCandleCount: number,
    security: Security,
    timeBucket: SuperChartConfiguration_TimeBucket,
    referenceTime: number,
    accurateLastCandle: boolean = false,
    dontGiveExtraCandles: boolean = false
): GetCandlesRequestV2 => {
    return {
        backwardCandleCount,
        dontGiveExtraCandles: (import.meta.env.MODE ?? '') === 'production' ? dontGiveExtraCandles : true,
        forwardCandleCount: forwardCandleCount,
        security,
        timeBucket: {
            timeSeries: timeBucket.timeSeries,
            value: timeBucket.timeValue
        },
        referenceTime,
        // accurateLastCandle
    } as any
}

export const getTimeWidthInSeconds = (timeBucket: SuperChartConfiguration_TimeBucket): number => {
    let timeWidth: number = timeBucket.timeValue
    switch (timeBucket.timeSeries) {
        case TimeBucket_TimeSeries.SECOND: {
            timeWidth *= 1
            break
        }
        case TimeBucket_TimeSeries.MINUTE: {
            timeWidth *= 60
            break
        }
        case TimeBucket_TimeSeries.HOUR: {
            timeWidth *= (60 * 60)
            break
        }
        case TimeBucket_TimeSeries.DAY: {
            timeWidth *= (60 * 60 * 24)
            break
        }
        case TimeBucket_TimeSeries.WEEK: {
            timeWidth *= (60 * 60 * 24 * 7)
            break
        }
        default: {
            timeWidth *= 1
        }
    }
    return timeWidth
}

export const getTimeWidthInMilliSeconds = (timeBucket: SuperChartConfiguration_TimeBucket): number => getTimeWidthInSeconds(timeBucket) * 1000
